import { useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useState, useRef } from 'react';
import useDebounce from './useDebounce';

const useDebouncedQuery = (query, variables, delay = 500, skipQuery = false) => {
  const [isPending, setIsPending] = useState(false);
  const [getData, { data, loading, error }] = useLazyQuery(query, variables);

  const variablesRef = useRef(null);

  const executeQuery = useCallback(() => {
    if (variablesRef.current) {
      getData({ variables: variablesRef.current });
    }
  }, [getData]);

  const debouncedExecuteQuery = useDebounce(executeQuery, delay);

  useEffect(() => {
    const prevVariables = variablesRef.current;
    const currentVariables = variables;

    if (JSON.stringify(prevVariables) !== JSON.stringify(currentVariables) && !skipQuery) {
      variablesRef.current = currentVariables;
      setIsPending(true);
      debouncedExecuteQuery();
    }
  }, [variables, debouncedExecuteQuery, skipQuery]);

  useEffect(() => {
    if ((loading && isPending) || data) {
      setIsPending(false);
    }
  }, [loading, isPending, data]);

  const isLoading = loading || isPending;

  return {
    data,
    loading: isLoading,
    error,
    isPending,
    refetch: executeQuery,
  };
};

export default useDebouncedQuery;
